import React, { FC } from 'react'
import { Controller, FieldErrors } from 'react-hook-form'
import { IControlledSelectProps } from 'transfix-form/components/ControlledSelect'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import { Box } from 'transfix-ui/core'
import { ICarrierApplication } from 'types'
import { Maybe } from 'types/graphqlTypes'
import { TRACKING_METHOD_TYPE } from 'types/tracking'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { ControlledTrailer } from 'apps/fleet-planner/components/create-load/ControlledTrailer'
import { ITruck } from 'apps/fleet-planner/types'
import { TruckAutoSuggest } from './TruckAutosuggest'
import { TRateConfirmationFormSchema } from './components/RateConfirmationForm/schema/RateConfirmationFormSchema'
import { TInputRefType } from './components/RateConfirmationForm/types'

export interface IEquipmentDetailOptionsProps
  extends Pick<IControlledSelectProps, 'control' | 'SelectProps'> {
  carrierApplication: Maybe<ICarrierApplication>
  selectedMotorCarrierId?: number
  vinRequired?: boolean
  formErrors: FieldErrors<TRateConfirmationFormSchema>
  vinInputRef?: TInputRefType['vin']
  onTruckChange: (selectedTruck: Partial<ITruck> | undefined) => void
  onTrailerChange: (selectedTrailerNumber?: string) => void
  selectedTrackingMethod: TRACKING_METHOD_TYPE
}

export const EquipmentDetailOptions: FC<IEquipmentDetailOptionsProps> = ({
  carrierApplication,
  control,
  formErrors,
  onTrailerChange,
  onTruckChange,
  selectedMotorCarrierId,
  selectedTrackingMethod,
  vinInputRef,
  vinRequired,
}) => {
  const fpTrailerOnRateConPage = useFeatureFlag('fpTrailerOnRateConPage')

  const { id: carrierApplicationId } = carrierApplication || {}

  return (
    <>
      {carrierApplicationId && (
        <Box mt={2}>
          <Controller
            control={control}
            name='truckNumber'
            render={({ onBlur, onChange, value }) => {
              const changeTruckNumberHandler = (selectedTruck?: Partial<ITruck>) => {
                onChange(selectedTruck?.truckNumber)
                if (onTruckChange) {
                  onTruckChange(selectedTruck)
                }
              }
              const changeTruckNumberInputHandler = (truckNumber = '') => {
                onChange(truckNumber)
                if (onTruckChange) {
                  onTruckChange({})
                }
              }
              return (
                <TruckAutoSuggest
                  selectedTrackingMethod={selectedTrackingMethod}
                  selectedMotorCarrierId={selectedMotorCarrierId}
                  label='Truck #'
                  onBlur={onBlur}
                  value={value}
                  onInputChange={changeTruckNumberInputHandler}
                  onChange={changeTruckNumberHandler}
                  dataTest='truck-number'
                />
              )
            }}
          />
        </Box>
      )}
      {vinRequired && (
        <Box mt={2}>
          <ControlledTextField
            control={control}
            name='vin'
            TextInputProps={{
              large: true,
              label: 'Tractor VIN (last 4 digits)',
              placeholder: ' — — — —',
              type: 'number',
              error: !!formErrors?.vin?.message,
              validationMessage: formErrors?.vin?.message,
              inputRef: vinInputRef,
            }}
          />
          <Box component='legend' className='label'>
            Your insurance policy requires that we collect your truck’s VIN number.
          </Box>
        </Box>
      )}
      <Box mt={2}>
        {carrierApplicationId &&
          (fpTrailerOnRateConPage ? (
            <ControlledTrailer
              useNumber
              name='trailerNumber'
              control={control}
              setValue={(name, value) => {
                if (name === 'trailerNumber') onTrailerChange(value)
              }}
              TrailerProps={{
                error: formErrors?.trailerNumber?.message,
                large: true,
                refetchOptions: true,
                selectedMotorCarrierId,
              }}
            />
          ) : (
            <ControlledTextField
              control={control}
              name='trailerNumber'
              TextInputProps={{
                large: true,
                label: 'Trailer Number',
                placeholder: 'Enter your trailer number (optional)',
                error: !!formErrors?.trailerNumber?.message,
                validationMessage: formErrors?.trailerNumber?.message,
              }}
            />
          ))}
      </Box>
    </>
  )
}
